import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {useTheme} from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles, createMuiTheme} from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import orange from '@material-ui/core/colors/orange';
import amber from '@material-ui/core/colors/amber';
import green from '@material-ui/core/colors/green';
import blue from '@material-ui/core/colors/blue';
import indigo from '@material-ui/core/colors/indigo';
import deepPurple from '@material-ui/core/colors/deepPurple';
import grey from '@material-ui/core/colors/grey';
import {useDispatch} from 'react-redux';
import SubmitButton from '&/components/SubmitButton';
import {changeTheme} from '&/store/app/actions';
import {Button} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
	formControl: {
		margin: theme.spacing.unit * 3,
	},
	group: {
		margin: `${theme.spacing.unit}px 0`,
	},
	redRoot: {
		color: red[600],
		'&$checked': {
			color: red[500],
		},
	},
	orangeRoot: {
		color: orange[600],
		'&$checked': {
			color: orange[500],
		},
	},
	amberRoot: {
		color: amber[600],
		'&$checked': {
			color: amber[500],
		},
	},
	greenRoot: {
		color: green[600],
		'&$checked': {
			color: green[500],
		},
	},
	blueRoot: {
		color: blue[600],
		'&$checked': {
			color: blue[500],
		},
	},
	indigoRoot: {
		color: indigo[600],
		'&$checked': {
			color: indigo[500],
		},
	},
	deepPurpleRoot: {
		color: deepPurple[600],
		'&$checked': {
			color: deepPurple[500],
		},
	},
	blackRoot: {
		color: grey[600],
		'&$checked': {
			color: grey[500],
		},
	},
	checked: {},
	unlock: {
		marginTop: '30px',
		marginBottom: '20px',
		justifyContent: 'center',
		alignSelf: 'center',
	},
}));

const ThemePicker = ({open, closeModal}) => {
	const classes = useStyles();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

	const getColorString = value => {
		switch (value) {
			case red['500']:
				return 'red';
			case orange['500']:
				return 'orange';
			case amber['500']:
				return 'amber';
			case green['500']:
				return 'green';
			case blue['500']:
				return 'blue';
			case indigo['500']:
				return 'indigo';
			case deepPurple['500']:
				return 'deepPurple';
			case grey['500']:
				return 'darkTheme';
			default:
				return null;
		}
	};

	const getColor = value => {
		switch (value) {
			case 'red':
				return red;
			case 'orange':
				return orange;
			case 'amber':
				return amber;
			case 'green':
				return green;
			case 'blue':
				return blue;
			case 'indigo':
				return indigo;
			case 'deepPurple':
				return deepPurple;
			case 'black':
				return grey;
			default:
				return null;
		}
	};

	const [typeTheme, setTypeTheme] = useState(theme.palette.type);
	const [primaryColor, setPrimaryColor] = useState(getColorString(theme.palette.primary.main));

	const dispatch = useDispatch();

	const handleChangeTypeTheme = e => {
		setTypeTheme(e.target.value);

		dispatch(
			changeTheme({
				type: e.target.value,
				primary: getColor(primaryColor),
			}),
		);
	};

	const handleChangePrimaryColor = e => {
		setPrimaryColor(e.target.value);

		dispatch(
			changeTheme({
				type: typeTheme,
				primary: getColor(e.target.value),
			}),
		);
	};

	return (
		<Dialog open={open} onClose={closeModal}>
			<DialogTitle>Appearance</DialogTitle>

			<DialogContent>
				<FormControl component='fieldset' className={classes.formControl}>
					<FormLabel focused component='legend'>
						Theme
					</FormLabel>
					<RadioGroup value={typeTheme} className={classes.group} onChange={handleChangeTypeTheme}>
						<FormControlLabel value='light' control={<Radio color='primary' />} label='Light' />
						<FormControlLabel value='dark' control={<Radio color='primary' />} label='Dark' />
					</RadioGroup>
				</FormControl>
				<FormControl component='fieldset' className={classes.formControl}>
					<FormLabel focused component='legend'>
						Accent
					</FormLabel>
					<RadioGroup
						className={classes.group}
						value={primaryColor}
						onChange={handleChangePrimaryColor}
					>
						<FormControlLabel
							value='red'
							control={
								<Radio
									classes={{
										root: classes.redRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Red'
						/>
						<FormControlLabel
							value='orange'
							control={
								<Radio
									classes={{
										root: classes.orangeRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Orange'
						/>
						<FormControlLabel
							value='amber'
							control={
								<Radio
									classes={{
										root: classes.amberRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Amber'
						/>
						<FormControlLabel
							value='green'
							control={
								<Radio
									classes={{
										root: classes.greenRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Green'
						/>
						<FormControlLabel
							value='blue'
							control={
								<Radio
									classes={{
										root: classes.blueRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Blue'
						/>
						<FormControlLabel
							value='indigo'
							control={
								<Radio
									classes={{
										root: classes.indigoRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Indigo'
						/>
						<FormControlLabel
							value='deepPurple'
							control={
								<Radio
									classes={{
										root: classes.deepPurpleRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Deep Purple'
						/>
						<FormControlLabel
							value='black'
							control={
								<Radio
									classes={{
										root: classes.blackRoot,
										checked: classes.checked,
									}}
								/>
							}
							label='Grey'
						/>
					</RadioGroup>
				</FormControl>
				<SubmitButton
					className={classes.unlock}
					variant='outlined'
					color='primary'
					//disabled={disabled}
					onClick={closeModal}
					//loading={loading}
				>
					{' '}
					Okay!
				</SubmitButton>
			</DialogContent>
		</Dialog>
	);
};

ThemePicker.propTypes = {
	open: PropTypes.bool,
	theme: PropTypes.object,
	closeModal: PropTypes.func,
};

export default ThemePicker;
